import { useState } from 'react';
import PropTypes from 'prop-types';
import Icon from 'Components/Atoms/Icon/Icon';
import fileCheckImage from './assets/file-check.svg';
import fileErrorImage from './assets/file-error.svg';
import fileGrayImage from './assets/file-gray.svg';
import filePrimaryImage from './assets/file-primary.svg';
import { formatDateISO } from 'Utils/formatters';
import useLocale from 'Hooks/useLocale';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Spinner from 'Components/Atoms/Spinner';
import { deleteInvoiceEmail, processInvoiceEmail } from 'Services/invoices';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';
import { trackError } from 'Utils/errorMonitoring';
import IconButton from 'Components/Atoms/IconButton/IconButton';

export default function InvoicesInboxItem(props) {
    const { onView, files, sender, date, subject, id } = props;

    const [processing, setProcessing] = useState(false);
    const [retrying, setRetrying] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    const { t } = useTranslation('invoices');
    const locale = useLocale();
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();

    const handleRetry = async () => {
        setRetrying(true);
        try {
            await processInvoiceEmail(id);
            enqueueSnackbar(t('reprocessing-email'), { key: id, variant: 'processing' });
        } catch (error) {
            trackError(error);
        }
    };

    const handleDelete = async () => {
        setProcessing(true);
        try {
            await deleteInvoiceEmail(id);
            await queryClient.invalidateQueries({ queryKey: ['invoices', 'emails'] });
        } catch (error) {
            showUnknownError(error);
        } finally {
            setProcessing(true);
        }
    };

    const title = `${sender} · ${formatDateISO(date, { locale })}`;

    return (
        <div className="relative">
            <div
                className={clsx(
                    'ring-1 ring-gray-100 shadow-sm rounded-lg p-3',
                    processing && 'opacity-25 pointer-events-none',
                )}
            >
                <div className="grid gap-2 grid-cols-[1fr_auto] items-center min-w-0">
                    <div className="min-w-0">
                        <div className="font-semibold text-sm cursor-pointer" onClick={onView}>
                            {subject}
                        </div>

                        <div className="text-xs text-gray-400 truncate" title={title}>
                            {title}
                        </div>
                    </div>

                    <div className="flex gap-1" onClick={(ev) => ev.stopPropagation()}>
                        <IconButton
                            variant="ghost"
                            size="small"
                            icon={<Icon name="eye" size={18} />}
                            tooltip={t('view-email')}
                            onClick={onView}
                        />

                        <IconButton
                            variant="ghost"
                            size="small"
                            icon={<Icon name="refresh" size={18} />}
                            tooltip={t('retry')}
                            onClick={handleRetry}
                            disabled={retrying}
                        />

                        <IconButton
                            variant="ghost"
                            size="small"
                            icon={<Icon name="trash" size={18} />}
                            tooltip={t('delete')}
                            onClick={handleDelete}
                        />
                    </div>
                </div>

                {files.length > 0 && (
                    <div className="pt-3">
                        <ul className="flex flex-wrap gap-1 items-center text-gray-500">
                            {files.map((file, index) => (
                                <li
                                    key={index}
                                    className="text-xs flex max-w-[220px] items-center gap-2 rounded-full px-2 py-0.5 bg-white border border-gray-200"
                                >
                                    <span className="flex flex-shrink-0">
                                        <img src={file.status === 'OK' ? filePrimaryImage : fileGrayImage} alt="" />
                                    </span>

                                    <span className="truncate">{file.name}</span>

                                    <span className="flex flex-shrink-0">
                                        <img src={file.status === 'OK' ? fileCheckImage : fileErrorImage} alt="" />
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            {processing && (
                <div className="absolute inset-0 flex justify-center items-center">
                    <Spinner size={24} />
                </div>
            )}
        </div>
    );
}

InvoicesInboxItem.propTypes = {
    id: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
    sender: PropTypes.string.isRequired,
    date: PropTypes.instanceOf(Date).isRequired,
    files: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            status: PropTypes.string.isRequired,
            status_message: PropTypes.shape({
                description: PropTypes.string.isRequired,
            }),
        }),
    ).isRequired,
    onView: PropTypes.func.isRequired,
};
